import { forwardRef, useEffect, useRef } from "react";

const MAX_SOLDIERS_DESKTOP = 40;
const MAX_SOLDIERS_MOBILE = 15;
const MIN_SOLDIERS_PER_BAND = 1;
const WOUND_MS = 600;
const SHAKE_MS = 240;

const BattleCanvas = forwardRef(function BattleCanvas(
  { bookRef, hitsRef, mobile = false, heightClass = "h-[70vh]", topClass = "top-[18vh]" },
  ref,
) {
  const localRef = useRef(null);
  const canvasRef = ref || localRef;
  const spritesRef = useRef({ bull: null, bear: null, loaded: false });
  const modeRef = useRef({ mobile });
  useEffect(() => {
    modeRef.current.mobile = mobile;
  }, [mobile]);

  useEffect(() => {
    const loadImg = (src) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
      });
    Promise.all([
      loadImg("/sprites/bull_soldier.png"),
      loadImg("/sprites/bear_soldier.png"),
    ]).then(([bull, bear]) => {
      spritesRef.current = { bull, bear, loaded: true };
    });
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf = 0;
    let running = true;
    const wounds = [];
    const shake = { until: 0, magnitude: 0 };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let lastFrame = 0;
    const targetFps = 30;
    const frameInterval = 1000 / targetFps;
    const loop = (t) => {
      if (!running) return;
      if (t - lastFrame >= frameInterval) {
        lastFrame = t;
        drawFrame(ctx, canvas, bookRef.current, hitsRef, wounds, shake, spritesRef.current, t, modeRef.current);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [bookRef, hitsRef, canvasRef]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute ${topClass} left-0 w-full ${heightClass} z-10 block`}
      data-testid="battlefield-canvas"
      aria-label="Live order book rendered as a battlefield"
    />
  );
});

export default BattleCanvas;

function drawFrame(ctx, canvas, book, hitsRef, wounds, shake, sprites, t, mode) {
  const rect = canvas.getBoundingClientRect();
  const W = rect.width;
  const H = rect.height;

  drainHits(hitsRef, wounds, shake, book, t);
  for (let i = wounds.length - 1; i >= 0; i--) {
    if (t - wounds[i].startedAt > WOUND_MS) wounds.splice(i, 1);
  }

  ctx.save();
  if (t < shake.until) {
    const p = (shake.until - t) / SHAKE_MS;
    const mag = shake.magnitude * p;
    ctx.translate((Math.random() - 0.5) * mag * 2, (Math.random() - 0.5) * mag * 2);
  }

  ctx.clearRect(-16, -16, W + 32, H + 32);

  const { bids = [], asks = [] } = book || {};
  const midPrice = book?.midPrice;
  const centerX = W / 2;

  let maxQty = 0;
  for (const l of bids) if (l.qty > maxQty) maxQty = l.qty;
  for (const l of asks) if (l.qty > maxQty) maxQty = l.qty;
  if (maxQty <= 0) maxQty = 1;

  const bandCount = Math.max(bids.length, asks.length, 1);
  const bandHeight = Math.max(mode.mobile ? 12 : 14, Math.min(mode.mobile ? 28 : 36, H / (bandCount + 2)));
  const armyTop = (H - bandHeight * bandCount) / 2;

  drawArmy(ctx, {
    levels: bids, side: "bid", frontlineX: centerX - 6, outerX: 20,
    bandHeight, armyTop, maxQty, time: t, wounds, sprites, mode,
  });
  drawArmy(ctx, {
    levels: asks, side: "ask", frontlineX: centerX + 6, outerX: W - 20,
    bandHeight, armyTop, maxQty, time: t, wounds, sprites, mode,
  });

  drawFrontline(ctx, centerX, H, midPrice, t);
  drawArmyLabels(ctx, W);
  ctx.restore();
}

function drainHits(hitsRef, wounds, shake, book, t) {
  const arr = hitsRef?.current;
  if (!arr || arr.length === 0) return;
  const bidCount = book?.bids?.length || 0;
  const askCount = book?.asks?.length || 0;
  while (arr.length > 0) {
    const evt = arr.shift();
    const damage = Math.max(1, Math.min(3, evt.damage || 1));
    const bandUpper = Math.min(3, Math.max(1, evt.side === "bid" ? bidCount : askCount));
    for (let d = 0; d < damage; d++) {
      wounds.push({
        side: evt.side,
        band: Math.floor(Math.random() * bandUpper),
        slot: Math.floor(Math.random() * 4),
        startedAt: t,
      });
    }
    if (evt.severity && evt.severity >= 2) {
      shake.until = t + SHAKE_MS;
      shake.magnitude = 4;
    }
  }
}

function drawArmy(ctx, opts) {
  const { levels, side, frontlineX, outerX, bandHeight, armyTop, maxQty, time, wounds, sprites, mode } = opts;
  const isBull = side === "bid";
  const facing = isBull ? 1 : -1;
  const dir = isBull ? -1 : 1;
  const bandCount = levels.length;
  const maxSoldiers = mode.mobile ? MAX_SOLDIERS_MOBILE : MAX_SOLDIERS_DESKTOP;
  const soldierW = mode.mobile ? 12 : 14;
  const soldierH = mode.mobile ? 20 : 24;
  const gap = mode.mobile ? 3 : 4;

  for (let i = 0; i < bandCount; i++) {
    const level = levels[i];
    if (!level) continue;

    const y = armyTop + i * bandHeight;
    const rowCenterY = y + bandHeight / 2;

    const ratio = Math.min(1, level.qty / maxQty);
    const targetN = Math.max(MIN_SOLDIERS_PER_BAND, Math.round(ratio * maxSoldiers));
    const available = Math.abs(outerX - frontlineX) - 30;
    const maxFit = Math.floor(available / (soldierW + gap));
    const visible = Math.min(targetN, maxFit);
    const hiddenCount = targetN - visible;

    ctx.save();
    const bandAlpha = 0.05 + ratio * 0.15;
    ctx.fillStyle = isBull ? `rgba(0, 255, 163, ${bandAlpha})` : `rgba(255, 42, 42, ${bandAlpha})`;
    const bandX1 = isBull ? outerX : frontlineX;
    const bandX2 = isBull ? frontlineX : outerX;
    ctx.fillRect(Math.min(bandX1, bandX2), y + 1, Math.abs(bandX2 - bandX1), bandHeight - 2);
    ctx.restore();

    // Skip per-row price label on mobile to reduce clutter
    if (!mode.mobile) {
      ctx.save();
      ctx.fillStyle = isBull ? "rgba(0, 255, 163, 0.85)" : "rgba(255, 42, 42, 0.85)";
      ctx.font = "10px 'Azeret Mono', monospace";
      ctx.textBaseline = "middle";
      const labelX = isBull ? outerX + 4 : outerX - 4;
      ctx.textAlign = isBull ? "left" : "right";
      ctx.fillText(`${level.price.toFixed(2)}  ·  ${level.qty.toFixed(3)}`, labelX, rowCenterY);
      ctx.restore();
    }

    for (let s = 0; s < visible; s++) {
      const wound = findWound(wounds, side, i, s, time);
      const x = frontlineX + dir * (20 + s * (soldierW + gap));
      const bobY = Math.sin(time / 260 + i * 0.7 + s * 0.55) * 2;
      const swayX = Math.cos(time / 380 + i * 0.9 + s * 0.35) * 1;
      const baseY = rowCenterY - soldierH / 2 + bobY;
      const baseX = x + swayX;
      if (wound) {
        const w = (time - wound.startedAt) / WOUND_MS;
        drawWoundedSoldier(ctx, {
          x: baseX, y: baseY, isBull, facing, sprites, w: soldierW, h: soldierH,
          progress: Math.min(1, Math.max(0, w)),
        });
      } else {
        drawSoldier(ctx, { x: baseX, y: baseY, isBull, facing, sprites, w: soldierW, h: soldierH });
      }
    }

    if (hiddenCount > 0) {
      const badgeX = frontlineX + dir * (20 + visible * (soldierW + gap) + 6);
      ctx.save();
      ctx.font = "bold 10px 'Azeret Mono', monospace";
      ctx.fillStyle = isBull ? "#00FFA3" : "#FF2A2A";
      ctx.textBaseline = "middle";
      ctx.textAlign = isBull ? "right" : "left";
      ctx.fillText(`+${hiddenCount}`, badgeX, rowCenterY);
      ctx.restore();
    }
  }
}

function findWound(wounds, side, band, slot, time) {
  for (const w of wounds) {
    if (w.side === side && w.band === band && w.slot === slot) {
      if (time - w.startedAt <= WOUND_MS) return w;
    }
  }
  return null;
}

function drawSoldier(ctx, { x, y, isBull, facing, sprites, w, h }) {
  const img = isBull ? sprites?.bull : sprites?.bear;
  ctx.save();
  ctx.translate(x + w / 2, y + h / 2);
  ctx.scale(facing, 1);
  if (img) {
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
  } else {
    drawProceduralSoldier(ctx, w, h, isBull);
  }
  ctx.restore();
}

function drawWoundedSoldier(ctx, { x, y, isBull, facing, sprites, w, h, progress }) {
  const img = isBull ? sprites?.bull : sprites?.bear;
  const alpha = 1 - progress;
  const rot = progress * (isBull ? -Math.PI / 3 : Math.PI / 3);
  const dropY = progress * 10;
  const flashK = Math.max(0, 1 - progress * 1.6);

  ctx.save();
  ctx.globalAlpha = Math.max(0, alpha);
  ctx.translate(x + w / 2, y + h / 2 + dropY);
  ctx.rotate(rot);
  ctx.scale(facing, 1);
  if (img) {
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
  } else {
    drawProceduralSoldier(ctx, w, h, isBull);
  }
  if (flashK > 0) {
    ctx.globalCompositeOperation = "source-atop";
    ctx.fillStyle = `rgba(255, 255, 255, ${flashK * 0.9})`;
    ctx.fillRect(-w / 2, -h / 2, w, h);
  }
  ctx.restore();

  const puffR = 4 + progress * 10;
  const puffAlpha = 0.35 * (1 - progress);
  ctx.save();
  ctx.globalAlpha = Math.max(0, puffAlpha);
  ctx.fillStyle = "rgba(220, 220, 230, 0.9)";
  ctx.beginPath();
  ctx.arc(x + w / 2, y + h - 2 + dropY, puffR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawProceduralSoldier(ctx, w, h, isBull) {
  const primary = isBull ? "#00FFA3" : "#FF2A2A";
  const dark = isBull ? "#025939" : "#590909";
  const armor = "#0F172A";
  ctx.fillStyle = primary;
  ctx.fillRect(-w / 2 + 2, -h / 2 + 8, w - 4, h - 12);
  ctx.fillStyle = dark;
  ctx.fillRect(-w / 2 + 2, -h / 2 + 8, w - 4, 3);
  ctx.fillStyle = armor;
  ctx.beginPath();
  ctx.arc(0, -h / 2 + 6, 4, Math.PI, 2 * Math.PI);
  ctx.rect(-4, -h / 2 + 5, 8, 4);
  ctx.fill();
  ctx.fillStyle = primary;
  ctx.fillRect(-3, -h / 2 + 7, 6, 1);
  ctx.fillStyle = dark;
  ctx.fillRect(w / 2 - 5, -h / 2 + 11, 3, 8);
  ctx.fillStyle = primary;
  ctx.fillRect(w / 2 - 5, -h / 2 + 11, 3, 2);
  ctx.fillStyle = armor;
  ctx.fillRect(-w / 2 + 3, h / 2 - 4, 3, 4);
  ctx.fillRect(w / 2 - 6, h / 2 - 4, 3, 4);
}

function drawFrontline(ctx, x, H, price, t) {
  const pulse = 0.6 + 0.4 * Math.sin(t / 400);
  ctx.save();
  ctx.shadowColor = "#FFFFFF";
  ctx.shadowBlur = 30 * pulse + 10;
  ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
  ctx.fillRect(x - 1.5, 0, 3, H);
  ctx.restore();

  ctx.save();
  ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 6]);
  ctx.beginPath();
  ctx.moveTo(x, 0);
  ctx.lineTo(x, H);
  ctx.stroke();
  ctx.restore();

  if (price != null && !Number.isNaN(price)) {
    const text = price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    ctx.save();
    ctx.font = "bold 14px 'Unbounded', system-ui, sans-serif";
    const metrics = ctx.measureText(text);
    const padX = 10;
    const boxW = metrics.width + padX * 2;
    const boxH = 24;
    const boxX = x - boxW / 2;
    const boxY = H / 2 - boxH / 2;
    ctx.fillStyle = "rgba(1, 3, 8, 0.9)";
    ctx.strokeStyle = "rgba(255, 255, 255, 0.35)";
    ctx.lineWidth = 1;
    ctx.fillRect(boxX, boxY, boxW, boxH);
    ctx.strokeRect(boxX, boxY, boxW, boxH);
    ctx.fillStyle = "#FFFFFF";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillText(text, x, boxY + boxH / 2);
    ctx.restore();
  }
}

function drawArmyLabels(ctx, W) {
  ctx.save();
  ctx.font = "bold 11px 'Azeret Mono', monospace";
  ctx.textBaseline = "top";
  ctx.fillStyle = "rgba(0, 255, 163, 0.7)";
  ctx.textAlign = "left";
  ctx.fillText("BULLS · BIDS", 20, 8);
  ctx.fillStyle = "rgba(255, 42, 42, 0.7)";
  ctx.textAlign = "right";
  ctx.fillText("BEARS · ASKS", W - 20, 8);
  ctx.restore();
}
